import { Icon } from "#app/components/ui/icon.tsx";
import {
	CATEGORY_LABELS,
	type Category,
	type Frontmatter,
} from "#app/utils/blog-types.ts";

interface BlogHeaderProps {
	frontmatter: Frontmatter;
	readingTime: { text: string; minutes: number };
}

export function BlogHeader({ frontmatter, readingTime }: BlogHeaderProps) {
	const categoryLabel =
		CATEGORY_LABELS[frontmatter.category as Category] ??
		frontmatter.category;

	return (
		<header className="mb-8 border-b border-border pb-8">
			{/* Category badge */}
			<span className="mb-4 inline-block rounded-full bg-blue-50 px-3 py-1 text-body-2xs font-medium text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
				{categoryLabel}
			</span>

			{/* Title */}
			<h1
				className="mb-4 text-h2 sm:text-h1 text-foreground"
				style={{ fontFamily: "var(--serif-head)" }}
			>
				{frontmatter.title}
			</h1>

			{/* Description */}
			<p className="mb-6 text-body-lg text-muted-foreground">
				{frontmatter.description}
			</p>

			{/* Metadata row */}
			<div className="flex flex-wrap items-center gap-4 text-body-xs text-muted-foreground">
				<span className="font-medium text-foreground">
					{frontmatter.author.name}
				</span>
				<span className="flex items-center gap-1.5">
					<Icon name="calendar" className="size-3.5" />
					<time dateTime={frontmatter.date}>
						{new Date(frontmatter.date + "T00:00:00").toLocaleDateString(
							"en-US",
							{
								year: "numeric",
								month: "long",
								day: "numeric",
							},
						)}
					</time>
				</span>
				<span className="flex items-center gap-1.5">
					<Icon name="clock" className="size-3.5" />
					{readingTime.text}
				</span>
			</div>

			{/* Hero image */}
			{frontmatter.heroImage ? (
				<figure className="mt-8">
					<img
						src={frontmatter.heroImage.src}
						alt={frontmatter.heroImage.alt}
						className="w-full rounded-lg border border-border"
						loading="eager"
					/>
					{frontmatter.heroImage.credit ? (
						<figcaption className="mt-2 text-center text-body-2xs text-muted-foreground">
							{frontmatter.heroImage.credit}
						</figcaption>
					) : null}
				</figure>
			) : null}
		</header>
	);
}
